/* eslint-disable no-restricted-globals */
import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import DaumPostcode from "react-daum-postcode";

// components
import {
  TextInput,
  SelectBusinessType,
  NumberInput,
  TextArea,
} from "components/form";
import { Button } from "components/common";
import Popup from "components/ui/Popup";

// utils
import { api } from "utils";
import { compareObjects } from "utils/object";

// redux
import { updatePartnerInfo } from "redux/slices/partner/partnerSlice";

// hooks
import { usePrompt } from "hooks/usePrompt";

const InfoSettingsEditForm = ({
  info,
  goBackLevel,
  setGoBackLevel,
  initialHistoryLength,
  setLoading,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const popupRef = useRef(null);
  const [height, setHeight] = useState(window.innerHeight);
  const [open, setOpen] = useState(false);
  const [formikValues, setFormikValues] = useState({});
  const [dirty, setDirty] = useState(false);

  const initialValues = {
    partnerName: info?.partnerName || "",
    businessNumber: info?.businessNumber || "",
    businessType: info?.businessType || "",
    zipCode: info?.zipCode || "",
    address: info?.address || "",
    addressDetail: info?.addressDetail || "",
    telephone: info?.telephone || "",
    fax: info?.fax || "",
    homepage: info?.homepage || "",
    sns: info?.sns || "",
    toPartnerExplain: info?.toPartnerExplain || "",
  };

  const validationSchema = Yup.object().shape({
    partnerName: Yup.string().required("업체명을 반드시 입력해야합니다"),
    address: Yup.string().required("주소를 반드시 입력해야합니다"),
    telephone: Yup.string().required("전화번호를 반드시 입력해야합니다"),
  });

  const updateInfoSettings = async (values) => {
    setLoading(true);
    try {
      await api
        .patch("/partners/settings", {
          seqPartner: info?.seqPartner,
          seqPartnerBranch: info?.seqPartnerBranch,
          ...values,
        })
        .then(({ data }) => {
          if (info.partnerName !== values.partnerName)
            dispatch(updatePartnerInfo());
          setDirty(false);
          navigate(-(goBackLevel ? goBackLevel + 1 : 1));
          setLoading(false);
        });
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const handleZipCodeSearch = (data, setFieldValue, setFieldError) => {
    if (Object.keys(data).length) {
      setFieldValue("zipCode", data.zonecode);
      setFieldValue("address", data.address);
      setFieldError("address", null);
    }
    popupRef.current.close();
  };

  useEffect(() => {
    let userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.search("android") > -1) {
      window.visualViewport.addEventListener("resize", (e) => {
        const pageHeight = e.target.height;
        setHeight(pageHeight + scrollY);
      });
    }
    return;
  }, []);

  const handleSetRef = useCallback(
    (node) => {
      if (node?.values) setFormikValues(node?.values);
    },
    [info]
  );

  useEffect(() => {
    if (!compareObjects(formikValues, initialValues)) {
      setDirty(true);
    } else {
      setDirty(false);
    }
  }, [formikValues]);

  usePrompt(
    "현재 작성중인 내용이 있어요.",
    `이대로 이동하면 입력한 내용이\n저장되지 않아요.\n그래도 페이지에서 나갈까요?`,
    dirty
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnMount={false}
      onSubmit={(values) => updateInfoSettings(values)}
      innerRef={(node) => handleSetRef(node)}
    >
      {({ values, setFieldValue, setFieldError }) => (
        <Form>
          <Wrapper>
            <TextInput
              name="partnerName"
              label="업체명"
              placeholder="업체명을 입력해주세요"
            />

            <NumberInput
              inputType="basic"
              name="businessNumber"
              label="사업자번호"
              placeholder="- 없이 번호를 입력해주세요"
              disabled={true}
            />

            <SelectBusinessType
              name="businessType"
              label="사업자유형"
              disabled={true}
            />

            <SearchAddress>
              <TextInput
                name="zipCode"
                label="주소"
                placeholder="우편번호"
                marginBottom="0"
              />
              <Button
                type="button"
                variant="contained"
                className="square"
                onClick={() => setOpen(true)}
                text="검색"
                padding="14px 24px"
                borderRadius="8px"
                fontSize="16px"
              />
            </SearchAddress>

            <TextInput
              name="address"
              placeholder="도로명 주소"
              marginBottom="8px"
            />

            <TextInput name="addressDetail" placeholder="상세주소" />

            <NumberInput
              inputType="phone"
              name="telephone"
              label="전화번호"
              placeholder="- 없이 전화번호를 입력해주세요"
            />

            <NumberInput
              inputType="basic"
              name="fax"
              label="팩스번호"
              placeholder="- 없이 팩스번호를 입력해주세요"
            />

            <TextInput
              name="homepage"
              label="홈페이지 주소"
              placeholder="홈페이지 주소를 입력해주세요"
            />

            <TextInput
              name="sns"
              label="SNS"
              placeholder="SNS 주소를 입력해주세요"
            />

            <TextArea
              name="toPartnerExplain"
              label="오시는 길 설명"
              placeholder="센터에 찾아오는 방법을 입력해주세요"
              maxLength={250}
            />

            <SubmitButton>
              <Button
                type="submit"
                variant="contained"
                className="square"
                padding="16px"
                text="입력 완료"
              />
            </SubmitButton>
          </Wrapper>
          <Popup open={open} onClose={setOpen} padding="10px" ref={popupRef}>
            <PostCodeWrapper height={height}>
              <DaumPostcode
                defaultQuery={values.zipCode}
                onComplete={(data) =>
                  handleZipCodeSearch(data, setFieldValue, setFieldError)
                }
                onSearch={() => {
                  if (window.history.length - initialHistoryLength > 0) {
                    setGoBackLevel(++goBackLevel);
                  }
                }}
                style={postCodeStyle}
                errorMessage={(error) => console.error(error)}
              />
            </PostCodeWrapper>
          </Popup>
        </Form>
      )}
    </Formik>
  );
};

const postCodeStyle = {
  position: "relative",
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  zIndex: 999999,
};

const Wrapper = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 20px;
`;

const PostCodeWrapper = styled.div`
  height: ${(props) => `${props.height - 115}px`};
`;

const SubmitButton = styled.div`
  display: flex;
  margin-top: 32px;
`;

const SearchAddress = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin-bottom: 8px;

  div:first-child {
    flex: 1;
    margin-right: 8px;
  }
  button {
    max-width: 80px;
    max-height: 48px;
  }
`;

export default InfoSettingsEditForm;
